/**
 * IndustrialVerticalLifter3D — Industrial Vertical Continuous Transfer Lifter & Infeed Conveyor System.
 *
 * 100% faithful replication of the reference machine:
 * 1. Vertical Elevator Tower:
 *    - Multi-tier modular ladder/truss frame in brushed industrial stainless steel / aluminum.
 *    - 4-sided transparent safety polycarbonate / glass enclosure panels on all vertical tiers.
 *    - Vertical blue & green synchronous drive timing belts and chrome linear guide shafts.
 *    - Multiple internal lifting carriage shelves / transfer trays.
 *    - Side lifting eye-bolts / D-ring rigging lugs.
 *    - Top safety enclosure cage and cantilevered horizontal roller transfer bridge.
 * 2. Lower Base & Infeed Conveyor:
 *    - Slotted ventilation louver base skirt with pneumatic valve regulators & junction box.
 *    - Heavy-duty circular leveling jack feet with threaded stems.
 *    - Lower horizontal roller conveyor bed with side-mounted electric gear motors.
 *    - Conveyor transparent safety guard sneeze enclosure and tubular floor support legs.
 */

import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useRailElevation } from '../../railElevationContext';
import { FOUP } from '../FOUP/FOUP';
import { cameraDragStore } from '../../../../store/cameraDragStore';

export type IndustrialVerticalLifter3DProps = {
  id?: string;
  height?: number; // Total mast height (connects floor to overhead track)
  towerWidth?: number; // Width of the vertical elevator tower
  towerDepth?: number; // Depth of the vertical elevator tower
  conveyorLength?: number; // Length of the horizontal infeed conveyor
  conveyorWidth?: number; // Width of the infeed conveyor
  status?: 'RUNNING' | 'IDLE' | 'ERROR' | 'OFFLINE';
  animated?: boolean;
  progress01?: number; // External transfer progress (0..1)
  opacity?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  onSelect?: () => void;
};

export const IndustrialVerticalLifter3D: React.FC<IndustrialVerticalLifter3DProps> = React.memo(({
  id = 'VTU-LIFTER-01',
  height,
  towerWidth = 5.6,
  towerDepth = 5.6,
  conveyorLength = 10.5,
  conveyorWidth = 4.2,
  status = 'RUNNING',
  animated = true,
  progress01,
  opacity = 1,
  position = [0, 0, 0] as [number, number, number],
  rotation = [0, 0, 0] as [number, number, number],
  onSelect,
}) => {
  const dynamicRailY = useRailElevation();
  const effectiveHeight = Math.max(height ?? 0, dynamicRailY ?? 32.0);
  const carriageGroupRef = useRef<THREE.Group>(null);
  const conveyorRollersRef = useRef<THREE.Group>(null);

  // Dimensions
  const baseHeight = 3.6; // Lower vented base height
  const conveyorBedY = baseHeight + 0.8; // Elevation of infeed conveyor bed (around Y = 4.4)
  const mastHeight = effectiveHeight - baseHeight; // Height of tower above vented base
  const mastCenterY = baseHeight + mastHeight * 0.5;

  const colSize = 0.28; // Tower corner column width
  const tierCount = 4; // Number of intermediate modular tier levels
  const tierHeight = mastHeight / tierCount;

  const halfTW = towerWidth * 0.5;
  const halfTD = towerDepth * 0.5;

  // Materials matching reference photo
  const materials = useMemo(() => {
    return {
      frameMetal: new THREE.MeshStandardMaterial({
        color: '#94A3B8', // Industrial satin gray / stainless steel
        metalness: 0.85,
        roughness: 0.25,
        envMapIntensity: 1.4,
        transparent: opacity < 1,
        opacity,
      }),
      darkFrameMetal: new THREE.MeshStandardMaterial({
        color: '#475569', // Dark steel accents & brackets
        metalness: 0.88,
        roughness: 0.3,
        envMapIntensity: 1.2,
      }),
      sheetMetalVented: new THREE.MeshStandardMaterial({
        color: '#CBD5E1', // Vented lower skirt panel
        metalness: 0.8,
        roughness: 0.35,
      }),
      chromeShaft: new THREE.MeshStandardMaterial({
        color: '#F1F5F9', // Polished linear guide shafts & rollers
        metalness: 0.98,
        roughness: 0.1,
        envMapIntensity: 2.2,
      }),
      clearPolycarbonate: new THREE.MeshPhysicalMaterial({
        color: '#FFFFFF',
        transmission: 0.93,
        opacity: 0.32,
        transparent: true,
        roughness: 0.08,
        metalness: 0.05,
        ior: 1.49,
      }),
      driveBeltBlue: new THREE.MeshStandardMaterial({
        color: '#0284C7', // Blue timing drive belt
        roughness: 0.4,
        metalness: 0.2,
      }),
      driveBeltGreen: new THREE.MeshStandardMaterial({
        color: '#10B981', // Green timing drive belt
        roughness: 0.4,
        metalness: 0.2,
      }),
      gearMotorDark: new THREE.MeshStandardMaterial({
        color: '#1E293B', // Dark navy/charcoal electric gear motor pods
        metalness: 0.7,
        roughness: 0.35,
      }),
      carriagePlate: new THREE.MeshStandardMaterial({
        color: '#E2E8F0', // White/silver carrier platforms
        metalness: 0.85,
        roughness: 0.2,
      }),
      statusLedCyan: new THREE.MeshStandardMaterial({
        color: '#00F0FF',
        emissive: '#00F0FF',
        emissiveIntensity: 1.2,
        toneMapped: false,
      }),
    };
  }, [opacity]);

  // Continuous loop animation of multiple carrier shelves rising/cycling vertically
  useFrame((state) => {
    if (!animated) return;
    if (cameraDragStore.isDragging()) return;
    const t = state.clock.getElapsedTime();
    if (carriageGroupRef.current) {
      if (progress01 !== undefined) {
        // Controlled by external timeline
        const stroke = mastHeight - 2.0;
        carriageGroupRef.current.position.y = baseHeight + 1.0 + progress01 * stroke;
      } else if (status === 'RUNNING') {
        // Continuous cycle
        const stroke = mastHeight - 2.0;
        const cycle = (t * 0.45) % 1.0;
        carriageGroupRef.current.position.y = baseHeight + 1.0 + cycle * stroke;
      }
    }
  });

  return (
    <group
      name={`IndustrialVerticalLifter-${id}`}
      position={position}
      rotation={rotation}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
    >
      {/* ========================================================================= */}
      {/* 1. LOWER VENTILATED BASE SKIRT & LEVELING FEET (Y = 0 TO baseHeight)      */}
      {/* ========================================================================= */}
      <group position={[0, baseHeight * 0.5, 0]}>
        {/* Main Lower Vented Base Skirt */}
        <mesh material={materials.sheetMetalVented} castShadow receiveShadow>
          <boxGeometry args={[towerWidth, baseHeight, towerDepth]} />
        </mesh>

        {/* Slotted Ventilation Louver Rows (Front & Sides) */}
        {[-baseHeight * 0.3, -baseHeight * 0.1, baseHeight * 0.1].map((ly, rIdx) =>
          [-1.4, -0.7, 0, 0.7, 1.4].map((lx, cIdx) => (
            <mesh
              key={`vent-slot-${rIdx}-${cIdx}`}
              position={[lx, ly, towerDepth * 0.5 + 0.02]}
              material={materials.gearMotorDark}
            >
              <boxGeometry args={[0.45, 0.1, 0.02]} />
            </mesh>
          ))
        )}

        {/* Side Pneumatic Regulator & Solenoid Valve Manifold */}
        <group position={[-towerWidth * 0.5 - 0.15, 0, 0]}>
          <mesh material={materials.chromeShaft}>
            <boxGeometry args={[0.2, 0.8, 0.6]} />
          </mesh>
          <mesh position={[-0.15, 0.2, 0]} material={materials.darkFrameMetal}>
            <cylinderGeometry args={[0.12, 0.12, 0.4, 16]} />
          </mesh>
          {/* Pressure gauge dial */}
          <mesh position={[-0.15, 0.2, 0.22]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.14, 0.14, 0.06, 16]} />
            <meshStandardMaterial color="#FFFFFF" />
          </mesh>
        </group>
      </group>

      {/* 4 Heavy-Duty Circular Leveling Jack Feet with Ground Anchor Plates */}
      {[-1, 1].flatMap((sx) =>
        [-1, 1].map((sz) => (
          <group
            key={`foot-${sx}-${sz}`}
            position={[(halfTW - 0.4) * sx, 0, (halfTD - 0.4) * sz]}
          >
            {/* Ground Disc Pad */}
            <mesh position={[0, 0.06, 0]} material={materials.darkFrameMetal} receiveShadow>
              <cylinderGeometry args={[0.6, 0.6, 0.12, 24]} />
            </mesh>
            {/* Threaded Stem */}
            <mesh position={[0, 0.35, 0]} material={materials.chromeShaft}>
              <cylinderGeometry args={[0.12, 0.12, 0.6, 16]} />
            </mesh>
          </group>
        ))
      )}

      {/* ========================================================================= */}
      {/* 2. MULTI-TIER VERTICAL ELEVATOR TOWER FRAME (Ladder Structure)           */}
      {/* ========================================================================= */}
      {/* 4 Corner Stainless Steel Vertical Columns */}
      {[-1, 1].flatMap((sx) =>
        [-1, 1].map((sz) => (
          <mesh
            key={`mast-col-${sx}-${sz}`}
            position={[
              sx * (halfTW - colSize * 0.5),
              mastCenterY,
              sz * (halfTD - colSize * 0.5),
            ]}
            material={materials.frameMetal}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[colSize, mastHeight, colSize]} />
          </mesh>
        ))
      )}

      {/* Multi-Tier Horizontal Cross-Beam Collars (Every tier level) */}
      {Array.from({ length: tierCount + 1 }).map((_, i) => {
        const tierY = baseHeight + i * tierHeight;
        return (
          <group key={`tier-collar-${i}`} position={[0, tierY, 0]}>
            {/* Front & Rear Beams */}
            <mesh position={[0, 0, halfTD - colSize * 0.5]} material={materials.frameMetal} castShadow>
              <boxGeometry args={[towerWidth, colSize * 1.2, colSize]} />
            </mesh>
            <mesh position={[0, 0, -halfTD + colSize * 0.5]} material={materials.frameMetal} castShadow>
              <boxGeometry args={[towerWidth, colSize * 1.2, colSize]} />
            </mesh>
            {/* Left & Right Beams */}
            <mesh position={[-halfTW + colSize * 0.5, 0, 0]} material={materials.frameMetal} castShadow>
              <boxGeometry args={[colSize, colSize * 1.2, towerDepth - colSize * 2]} />
            </mesh>
            <mesh position={[halfTW - colSize * 0.5, 0, 0]} material={materials.frameMetal} castShadow>
              <boxGeometry args={[colSize, colSize * 1.2, towerDepth - colSize * 2]} />
            </mesh>

            {/* Side Lifting Rigging Eye-Bolts / D-Rings */}
            {i > 0 && i < tierCount && (
              <group position={[-halfTW - 0.12, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                <mesh material={materials.darkFrameMetal}>
                  <torusGeometry args={[0.25, 0.06, 12, 24]} />
                </mesh>
              </group>
            )}
          </group>
        );
      })}

      {/* ========================================================================= */}
      {/* 3. 4-SIDED TRANSPARENT POLYCARBONATE SAFETY ENCLOSURE PANELS              */}
      {/* ========================================================================= */}
      {Array.from({ length: tierCount }).map((_, i) => {
        const paneCenterY = baseHeight + (i + 0.5) * tierHeight;
        const paneH = tierHeight - colSize * 1.4;
        return (
          <group key={`tier-panes-${i}`}>
            {/* Front Glass Pane (+Z) */}
            <mesh position={[0, paneCenterY, halfTD - 0.04]} material={materials.clearPolycarbonate}>
              <boxGeometry args={[towerWidth - colSize * 2, paneH, 0.06]} />
            </mesh>
            {/* Rear Glass Pane (-Z) */}
            <mesh position={[0, paneCenterY, -halfTD + 0.04]} material={materials.clearPolycarbonate}>
              <boxGeometry args={[towerWidth - colSize * 2, paneH, 0.06]} />
            </mesh>
            {/* Left Glass Pane (-X) */}
            <mesh position={[-halfTW + 0.04, paneCenterY, 0]} material={materials.clearPolycarbonate}>
              <boxGeometry args={[0.06, paneH, towerDepth - colSize * 2]} />
            </mesh>
            {/* Right Glass Pane (+X) - open at lower infeed tier for conveyor passage */}
            {(i > 0 || paneCenterY > conveyorBedY + 3.0) && (
              <mesh position={[halfTW - 0.04, paneCenterY, 0]} material={materials.clearPolycarbonate}>
                <boxGeometry args={[0.06, paneH, towerDepth - colSize * 2]} />
              </mesh>
            )}
          </group>
        );
      })}

      {/* ========================================================================= */}
      {/* 4. INTERNAL SYNCHRONOUS DRIVE BELTS & LINEAR GUIDE SHAFTS                 */}
      {/* ========================================================================= */}
      {/* Left Front Blue Drive Belt */}
      <mesh
        position={[-halfTW + 0.6, mastCenterY, halfTD - 0.6]}
        material={materials.driveBeltBlue}
      >
        <boxGeometry args={[0.08, mastHeight - 0.4, 0.16]} />
      </mesh>
      {/* Left Rear Blue Drive Belt */}
      <mesh
        position={[-halfTW + 0.6, mastCenterY, -halfTD + 0.6]}
        material={materials.driveBeltBlue}
      >
        <boxGeometry args={[0.08, mastHeight - 0.4, 0.16]} />
      </mesh>
      {/* Right Front Green Drive Belt */}
      <mesh
        position={[halfTW - 0.6, mastCenterY, halfTD - 0.6]}
        material={materials.driveBeltGreen}
      >
        <boxGeometry args={[0.08, mastHeight - 0.4, 0.16]} />
      </mesh>

      {/* Center Chrome Linear Precision Guide Shafts */}
      {[-1, 1].map((sz) => (
        <mesh
          key={`center-shaft-${sz}`}
          position={[0, mastCenterY, sz * (halfTD - 0.5)]}
          material={materials.chromeShaft}
          castShadow
        >
          <cylinderGeometry args={[0.1, 0.1, mastHeight - 0.4, 20]} />
        </mesh>
      ))}

      {/* ========================================================================= */}
      {/* 5. MULTI-SHELF CARRIER PLATFORMS (Dynamic Moving Lifter Carriages)        */}
      {/* ========================================================================= */}
      <group ref={carriageGroupRef} position={[0, baseHeight + 1.0, 0]}>
        {/* Render 3 Tiered Product Carrier Trays attached to the synchronous vertical belt */}
        {[0, -tierHeight * 0.85, -tierHeight * 1.7].map((offsetY, sIdx) => (
          <group key={`shelf-${sIdx}`} position={[0, offsetY, 0]}>
            {/* Horizontal Carrier Tray Floor Plate */}
            <mesh material={materials.carriagePlate} castShadow receiveShadow>
              <boxGeometry args={[towerWidth * 0.72, 0.16, towerDepth * 0.72]} />
            </mesh>
            {/* Vertical Backing Plate & Guide Slider */}
            <mesh position={[-towerWidth * 0.32, 0.8, 0]} material={materials.carriagePlate}>
              <boxGeometry args={[0.08, 1.4, towerDepth * 0.65]} />
            </mesh>
            {/* Linear Bearing Guide Sleeves */}
            {[-1, 1].map((sz) => (
              <mesh
                key={`sleeve-${sz}`}
                position={[0, 0, sz * (halfTD - 0.5)]}
                material={materials.chromeShaft}
              >
                <cylinderGeometry args={[0.18, 0.18, 0.4, 16]} />
              </mesh>
            ))}
            {/* Carrier Status Indicator Strip */}
            <mesh position={[0, 0.1, towerDepth * 0.34]}>
              <boxGeometry args={[towerWidth * 0.5, 0.04, 0.04]} />
              <meshStandardMaterial color="#00F0FF" emissive="#00F0FF" emissiveIntensity={0.8} />
            </mesh>
            {/* Real 300mm Semiconductor FOUP on Carrier Tray */}
            <FOUP position={[0, 0.08, 0]} scale={8.5} />
          </group>
        ))}
      </group>

      {/* ========================================================================= */}
      {/* 6. TOP CANTILEVERED ROLLER TRANSFER BRIDGE & SAFETY CAGE (Y = effectiveHeight) */}
      {/* ========================================================================= */}
      <group position={[0, effectiveHeight, 0]}>
        {/* Top Open Safety Glass Canopy Guard Cage */}
        <group position={[0, 1.5, 0]}>
          {/* Top Frame Posts */}
          {[-1, 1].flatMap((sx) =>
            [-1, 1].map((sz) => (
              <mesh
                key={`top-cage-col-${sx}-${sz}`}
                position={[sx * (halfTW - 0.1), 0, sz * (halfTD - 0.1)]}
                material={materials.frameMetal}
              >
                <boxGeometry args={[0.15, 3.0, 0.15]} />
              </mesh>
            ))
          )}
          {/* Top Perimeter Header Rails */}
          <mesh position={[0, 1.5, halfTD - 0.1]} material={materials.frameMetal}>
            <boxGeometry args={[towerWidth, 0.15, 0.15]} />
          </mesh>
          <mesh position={[0, 1.5, -halfTD + 0.1]} material={materials.frameMetal}>
            <boxGeometry args={[towerWidth, 0.15, 0.15]} />
          </mesh>
          <mesh position={[-halfTW + 0.1, 1.5, 0]} material={materials.frameMetal}>
            <boxGeometry args={[0.15, 0.15, towerDepth]} />
          </mesh>
          <mesh position={[halfTW - 0.1, 1.5, 0]} material={materials.frameMetal}>
            <boxGeometry args={[0.15, 0.15, towerDepth]} />
          </mesh>
          {/* Transparent Glass Safety Walls on Cage */}
          <mesh position={[0, 0, halfTD - 0.1]} material={materials.clearPolycarbonate}>
            <boxGeometry args={[towerWidth - 0.3, 2.8, 0.04]} />
          </mesh>
          <mesh position={[0, 0, -halfTD + 0.1]} material={materials.clearPolycarbonate}>
            <boxGeometry args={[towerWidth - 0.3, 2.8, 0.04]} />
          </mesh>
          <mesh position={[-halfTW + 0.1, 0, 0]} material={materials.clearPolycarbonate}>
            <boxGeometry args={[0.04, 2.8, towerDepth - 0.3]} />
          </mesh>
        </group>

        {/* Cantilevered Top Horizontal Outfeed Roller Bridge (Extends -X toward Track) */}
        <group position={[-halfTW - 2.2, -0.4, 0]}>
          {/* Cantilevered Side Structural Beam Plates */}
          {[-halfTD + 0.15, halfTD - 0.15].map((bz, bIdx) => (
            <mesh key={`top-bridge-beam-${bIdx}`} position={[0, 0, bz]} material={materials.frameMetal} castShadow>
              <boxGeometry args={[4.4, 0.8, 0.2]} />
            </mesh>
          ))}
          {/* Top Roller Conveyor Bed */}
          <mesh position={[0, -0.2, 0]} material={materials.sheetMetalVented}>
            <boxGeometry args={[4.2, 0.25, towerDepth - 0.6]} />
          </mesh>
          {/* Transfer Roller Cylinders */}
          {[-1.6, -1.0, -0.4, 0.2, 0.8, 1.4].map((rx, idx) => (
            <mesh key={`top-roller-${idx}`} position={[rx, 0.2, 0]} rotation={[Math.PI / 2, 0, 0]} material={materials.chromeShaft}>
              <cylinderGeometry args={[0.14, 0.14, towerDepth - 0.7, 16]} />
            </mesh>
          ))}
          {/* Triple White Guide Guard Rails above rollers */}
          {[-halfTD + 0.2, halfTD - 0.2].map((rz, rzIdx) =>
            [0.5, 0.8, 1.1].map((ry, ryIdx) => (
              <mesh key={`top-guard-${rzIdx}-${ryIdx}`} position={[0, ry, rz]} material={materials.sheetMetalVented}>
                <boxGeometry args={[4.4, 0.12, 0.08]} />
              </mesh>
            ))
          )}
          {/* Drive Motor for Top Bridge */}
          <group position={[1.4, -0.2, halfTD + 0.2]} rotation={[0, 0, Math.PI / 2]}>
            <mesh material={materials.gearMotorDark}>
              <cylinderGeometry args={[0.25, 0.25, 0.6, 16]} />
            </mesh>
          </group>
        </group>
      </group>

      {/* ========================================================================= */}
      {/* 7. HORIZONTAL INFEED ROLLER CONVEYOR BED (Extends +X from Lower Base)    */}
      {/* ========================================================================= */}
      <group position={[halfTW + conveyorLength * 0.5, conveyorBedY, 0]}>
        {/* Main Conveyor Side Channel Frames (Front & Rear) */}
        {[-halfTD + 0.2, halfTD - 0.2].map((cz, cIdx) => (
          <mesh key={`conv-channel-${cIdx}`} position={[0, 0, cz]} material={materials.frameMetal} castShadow>
            <boxGeometry args={[conveyorLength, 0.9, 0.22]} />
          </mesh>
        ))}

        {/* Lower Sheet Metal Bed Tray */}
        <mesh position={[0, -0.25, 0]} material={materials.sheetMetalVented} receiveShadow>
          <boxGeometry args={[conveyorLength - 0.1, 0.2, conveyorWidth]} />
        </mesh>

        {/* Array of Rotating Conveyor Rollers */}
        <group ref={conveyorRollersRef}>
          {Array.from({ length: 14 }).map((_, rIdx) => {
            const rx = -conveyorLength * 0.5 + (rIdx + 0.5) * (conveyorLength / 14);
            return (
              <mesh
                key={`roller-${rIdx}`}
                position={[rx, 0.28, 0]}
                rotation={[Math.PI / 2, 0, 0]}
                material={materials.chromeShaft}
                castShadow
              >
                <cylinderGeometry args={[0.15, 0.15, conveyorWidth - 0.4, 16]} />
              </mesh>
            );
          })}
        </group>

        {/* 4 Side-Mounted Electric Drive Gear Motors with Shaft Hubs (Front +Z Face) */}
        {[-3.2, -1.0, 1.2, 3.4].map((mx, mIdx) => (
          <group key={`motor-pod-${mIdx}`} position={[mx, -0.15, halfTD + 0.15]}>
            {/* Gearbox Body */}
            <mesh material={materials.gearMotorDark} castShadow>
              <boxGeometry args={[1.2, 0.7, 0.6]} />
            </mesh>
            {/* Motor Cylinder Housing */}
            <group position={[0.7, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <mesh material={materials.gearMotorDark} castShadow>
                <cylinderGeometry args={[0.3, 0.3, 0.8, 16]} />
              </mesh>
            </group>
            {/* Drive Hub Flange */}
            <mesh position={[0, 0, -0.32]} material={materials.chromeShaft}>
              <cylinderGeometry args={[0.18, 0.18, 0.2, 16]} />
            </mesh>
          </group>
        ))}

        {/* Conveyor Transparent Safety Sneeze Guard Railing Enclosure */}
        <group position={[0, 1.6, 0]}>
          {/* Sneeze Guard Frame Posts */}
          {[-conveyorLength * 0.35, conveyorLength * 0.35].map((gx, gIdx) =>
            [-halfTD + 0.2, halfTD - 0.2].map((gz, gzIdx) => (
              <mesh
                key={`guard-post-${gIdx}-${gzIdx}`}
                position={[gx, 0, gz]}
                material={materials.frameMetal}
              >
                <boxGeometry args={[0.12, 2.4, 0.12]} />
              </mesh>
            ))
          )}
          {/* Horizontal Top Header Bars */}
          <mesh position={[0, 1.2, -halfTD + 0.2]} material={materials.frameMetal}>
            <boxGeometry args={[conveyorLength * 0.72, 0.12, 0.12]} />
          </mesh>
          <mesh position={[0, 1.2, halfTD - 0.2]} material={materials.frameMetal}>
            <boxGeometry args={[conveyorLength * 0.72, 0.12, 0.12]} />
          </mesh>
          {/* Clear Acrylic Safety Panels */}
          <mesh position={[0, 0, -halfTD + 0.2]} material={materials.clearPolycarbonate}>
            <boxGeometry args={[conveyorLength * 0.7, 2.2, 0.04]} />
          </mesh>
          <mesh position={[0, 0, halfTD - 0.2]} material={materials.clearPolycarbonate}>
            <boxGeometry args={[conveyorLength * 0.7, 2.2, 0.04]} />
          </mesh>
        </group>

        {/* Tubular Conveyor Support Legs with Threaded Foot Pads */}
        {[-conveyorLength * 0.2, conveyorLength * 0.25].map((lx, lIdx) =>
          [-halfTD + 0.4, halfTD - 0.4].map((lz, lzIdx) => {
            const legH = conveyorBedY - 0.45;
            return (
              <group key={`conv-leg-${lIdx}-${lzIdx}`} position={[lx, -conveyorBedY * 0.5, lz]}>
                {/* Vertical Leg Tube */}
                <mesh material={materials.darkFrameMetal} castShadow>
                  <cylinderGeometry args={[0.12, 0.12, legH, 16]} />
                </mesh>
                {/* Leveling Foot Disc */}
                <mesh position={[0, -legH * 0.5 + 0.05, 0]} material={materials.frameMetal} receiveShadow>
                  <cylinderGeometry args={[0.38, 0.38, 0.1, 20]} />
                </mesh>
              </group>
            );
          })
        )}
      </group>
    </group>
  );
});

export default IndustrialVerticalLifter3D;
